import type { ReactNode } from "react"

interface FilterField {
  /** Dropdown options for the field selector */
  options: { value: string; label: string }[]
  /** Current selected option value */
  selectedOption: string
  onOptionChange: (value: string) => void
  /** Current text input value */
  inputValue: string
  onInputChange: (value: string) => void
}

interface HpiFilterBarProps {
  fields: FilterField[]
  onUpdate: () => void
  className?: string
}

export function HpiFilterBar({
  fields,
  onUpdate,
  className = "",
}: HpiFilterBarProps) {
  return (
    <div
      className={`bg-hpi-pale-gray flex items-center gap-4 px-4 py-3 ${className}`}
    >
      {fields.map((field, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="relative">
            <select
              value={field.selectedOption}
              onChange={(e) => field.onOptionChange(e.target.value)}
              className="border-hpi-med-gray text-hpi-dark-gray focus:border-hpi-active-blue focus:ring-hpi-active-blue h-8 rounded border bg-white pr-7 pl-2 text-sm focus:ring-1"
            >
              {field.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <span className="text-hpi-dark-gray text-sm">is</span>
          <input
            type="text"
            value={field.inputValue}
            onChange={(e) => field.onInputChange(e.target.value)}
            className="border-hpi-med-gray text-hpi-dark-gray focus:border-hpi-active-blue focus:ring-hpi-active-blue h-8 w-32 rounded border bg-white px-2 text-sm focus:ring-1"
          />
        </div>
      ))}
      <button
        onClick={onUpdate}
        className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-dark-blue h-8 rounded border px-5 text-sm font-medium transition-colors hover:text-white"
      >
        Update
      </button>
    </div>
  )
}

/* ── Shared input class for filter fields ────────────────── */

const filterBaseClass =
  "rounded border border-hpi-med-gray bg-white px-2 text-sm text-hpi-dark-gray focus:border-hpi-active-blue focus:ring-1 focus:ring-hpi-active-blue"

/** Fixed height works for text/date inputs */
const filterInputClass = `h-8 ${filterBaseClass}`

/** Selects need padding-based height to avoid text clipping */
const filterSelectClass = `py-1.5 ${filterBaseClass}`

const filterLabelClass = "mb-1 block text-xs font-medium text-hpi-dark-gray"

/* ── Date Range Filter ───────────────────────────────────── */

interface HpiDateFilterBarProps {
  startDate: string
  onStartDateChange: (value: string) => void
  endDate: string
  onEndDateChange: (value: string) => void
  onUpdate: () => void
  /** Additional filter fields rendered between dates and Update button */
  children?: ReactNode
  className?: string
}

export function HpiDateFilterBar({
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  onUpdate,
  children,
  className = "",
}: HpiDateFilterBarProps) {
  return (
    <div
      className={`bg-hpi-pale-gray flex flex-wrap items-end gap-4 rounded-lg px-4 py-3 ${className}`}
    >
      <div>
        <label className={filterLabelClass}>Start Date</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => onStartDateChange(e.target.value)}
          className={filterInputClass}
        />
      </div>
      <div>
        <label className={filterLabelClass}>End Date</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => onEndDateChange(e.target.value)}
          className={filterInputClass}
        />
      </div>
      {children}
      <button
        onClick={onUpdate}
        className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-dark-blue h-8 rounded border px-5 text-sm font-medium transition-colors hover:text-white"
      >
        Update
      </button>
    </div>
  )
}

/* ── Reusable filter select field ────────────────────────── */

interface HpiFilterSelectProps {
  label: string
  value: string
  onChange: (value: string) => void
  options: { value: string | number; label: string }[]
  placeholder?: string
}

export function HpiFilterSelect({
  label,
  value,
  onChange,
  options,
  placeholder = "All",
}: HpiFilterSelectProps) {
  return (
    <div>
      <label className={filterLabelClass}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={filterSelectClass}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  )
}
